import { RawEnergyPlant, JaehrlicherErtrag, EnergyType, EnergyPlant, EnergyData } from '../types';

// Atomic: Parse year from field name
export function parseYearFromField(field: string): number | null {
  const match = field.match(/(\d{4})/);
  return match ? parseInt(match[1], 10) : null;
}

// Atomic: Transform raw plant data to our internal format
export function transformPlantData(rawPlant: RawEnergyPlant, type: EnergyType): EnergyPlant {
  const historischeErträge: JaehrlicherErtrag[] = [];
  
  // Extract historical yields
  Object.entries(rawPlant).forEach(([key, value]) => {
    const year = parseYearFromField(key);
    if (year && key.includes('Eingespeister Strom (kWh)') && typeof value === 'number') {
      historischeErträge.push({
        jahr: year,
        ertragKwh: value,
        leistungKw: Number(rawPlant['Engpassleistung (kW <sub>el</sub>)']) || 0
      });
    }
  });

  // Sort historical data by year
  historischeErträge.sort((a, b) => a.jahr - b.jahr);
  
  // Get the most recent year's data
  const latestYear = historischeErträge.length > 0 
    ? Math.max(...historischeErträge.map(e => e.jahr))
    : new Date().getFullYear() - 1;
    
  const latestErtrag = historischeErträge.find(e => e.jahr === latestYear) || 
    { ertragKwh: 0, leistungKw: 0, jahr: latestYear };

  // Create the plant with all required properties
  // Use kW power rating as the unique hash identifier
  const powerKw = Number(rawPlant['Engpassleistung (kW <sub>el</sub>)']) || 0;
  
  // Use kW value as the hash ID - much simpler and more meaningful
  const uniqueId = `plant-${type}-${rawPlant.ID}-${powerKw}`;
  
  return {
    id: uniqueId,
    name: `${rawPlant.Technologie} ${rawPlant.Ort}`,
    plz: rawPlant.Plz.toString(),
    ort: rawPlant.Ort,
    leistungKw: Number(rawPlant['Engpassleistung (kW <sub>el</sub>)']) || 0,
    ertragKwh: latestErtrag.ertragKwh,
    typ: type === 'sonne' ? 'solar' : 
         type === 'wasser' ? 'wasser' :
         type === 'biomasse' ? 'biomasse' : 'biogas',
    inbetriebnahme: historischeErträge.length > 0 
      ? Math.min(...historischeErträge.map(e => e.jahr)).toString()
      : new Date().getFullYear().toString(),
    historischeErträge: [...historischeErträge]
  };
}

// Atomic: Create empty energy data structure
export function createEmptyEnergyData(): EnergyData {
  return {
    sonne: [],
    wasser: [],
    biomasse: [],
    biogas: []
  };
}

// Re-export types for convenience
export type { EnergyData } from '../types';
